import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Shield, ArrowLeft, User, HelpCircle, LogOut } from 'lucide-react';
import { supabase } from '../supabase/client';
import LogoWithFallback from '../components/LogoWithFallback';

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
      }
    };
    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
              <LogoWithFallback className="h-10 w-10 object-contain" />
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/help" className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                <HelpCircle className="w-4 h-4" />帮助中心
              </Link>
              <Link to="/profile" className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                <User className="w-4 h-4" />学员中心
              </Link>
              <div className="h-6 w-px bg-gray-200 mx-1" />
              {user ? (
                <>
                  <span className="text-sm text-gray-700">{user.phone}</span>
                  <button onClick={handleLogout} className="flex items-center gap-1 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <LogOut className="w-4 h-4" />退出
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="px-4 py-2 text-sm text-gray-600 hover:text-blue-600 transition-colors">登录</Link>
                  <Link to="/register" className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">注册</Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">隐私政策</h1>
          </div>

          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 mb-6">最后更新日期：2024年4月</p>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. 引言</h2>
              <p className="text-gray-600">编程优题库（"我们"、"本平台"）重视用户的隐私保护。本隐私政策说明我们如何收集、使用、存储和保护您的个人信息。</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">2. 信息收集</h2>
              <p className="text-gray-600 mb-2">我们可能收集以下信息：</p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>注册信息：手机号、姓名</li>
                <li>使用数据：学习记录、考试记录</li>
                <li>设备信息：IP地址、浏览器类型</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">3. 信息使用</h2>
              <p className="text-gray-600 mb-2">我们使用您的信息用于：</p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>提供教育服务和个性化推荐</li>
                <li>发送课程提醒和考试通知</li>
                <li>改进服务质量和用户体验</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">4. 信息保护</h2>
              <p className="text-gray-600">我们采用行业标准的安全措施保护您的信息，包括数据加密、访问控制等。</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">5. 联系我们</h2>
              <p className="text-gray-600">如有隐私相关问题，请联系：support@udo100.com</p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
